import {
  IUIPage,
  IUIUrval,
  IValue,
  UIUrvalConditionObjectType,
  ValueConditionObjectType,
} from '../../../../Types/UrvalTypes';
import mergeSort from '../../../../Utils/mergeSort';
import {
  IAttributeErrors,
  UrvalErrorEnum,
} from '../../Ekonomi/Rutiner/EkonomiskAnalys/Components/SideMenu/Components/UrvalOchSummering/Components/Urval/Utils/UrvalUtils';
import { IAttestKodbehorighetUrval } from '../Components/KonteringManager/KonteringContext';
import {
  IKodstrang,
  IKodstrangDel,
} from '../Components/KonteringManager/KonteringManager';
import { ICorrectnessKodbeh } from '../Components/KonteringManager/KonteringManagerUtils';
import {
  IKontodel,
  IKontoplan,
} from '../Components/KonteringManager/Query/KonteringManagerQuery';

export interface IConditionResult {
  isValid: boolean;
  values: IValue[];
  conditionAttributeName: string;
  errorType: UrvalErrorEnum;
}

export const koddelStartsWith = (
  koddel: IKodstrangDel,
  conditionValue: string | number
): boolean => {
  let conditionString: string;
  if (typeof conditionValue === 'number') {
    conditionString = conditionValue.toString();
  } else {
    conditionString = conditionValue;
  }
  return koddel.kontodel.startsWith(conditionString);
};

export const koddelInInterval = (
  koddel: IKodstrangDel | IKontodel,
  conditionValue: IValue
): boolean => {
  const value = conditionValue.value?.toString();
  const toValue = conditionValue.toValue?.toString();
  if (!toValue) {
    return koddel.kontodel === value;
  } else if (!value) {
    return koddel.kontodel <= toValue;
  } else {
    return koddel.kontodel >= value && koddel.kontodel <= toValue;
  }
};

const fillRemainingKoddelar = (
  kontoplan: IKontoplan,
  koddelar: IKodstrangDel[]
): IKodstrangDel[] => {
  //Fill koddelar with missing kodsträngar
  const tempArray: IKodstrangDel[] = [];
  for (let index = 0; index < kontoplan.kontodelDefs.length; index++) {
    if (!ordningsNummerAlreadyExist(koddelar, index)) {
      const emptyKodDel: IKodstrangDel = {
        id: '',
        ordningsNr: index + 1,
        benamning: 'Autoskapad',
        kontodel: '',
      };
      tempArray.push(emptyKodDel);
    }
  }

  koddelar = koddelar.concat(tempArray);

  return mergeSort(koddelar, kontodelarCompare);
};

const isConditionDefined = (values: IValue[]): boolean => {
  const isDefined = values.some(value => {
    return value.toValue || value.value;
  });

  return isDefined;
};

//Type guard för att kolla om toBeDetermined är en instans av ValueConditionObjectType
export function determineIfIsValueConditionObjectType(
  toBeDetermined: UIUrvalConditionObjectType
): toBeDetermined is ValueConditionObjectType {
  if ((toBeDetermined as ValueConditionObjectType).type) {
    return true;
  }
  return false;
}

const kontodelarCompare = (a: IKodstrangDel, b: IKodstrangDel) => {
  if (a.ordningsNr < b.ordningsNr) {
    return -1;
  } else if (a.ordningsNr > b.ordningsNr) {
    return 1;
  }
  return 0;
};

const ordningsNummerAlreadyExist = (
  koddelar: IKodstrangDel[],
  idIndex: number
): boolean => {
  let result = false;
  for (let index = 0; index < koddelar.length; index++) {
    const koddel = koddelar[index];

    if (koddel.ordningsNr === idIndex + 1) {
      result = true;
    }
  }
  return result;
};

export const kontodelInCondition = (
  koddel: IKodstrangDel | IKontodel,
  conditionsObject: ValueConditionObjectType,
  blankIsOk?: boolean
): IConditionResult => {
  let result: IConditionResult = {
    isValid: false,
    values: [],
    conditionAttributeName: conditionsObject.attribute.AttributeName,
    errorType: UrvalErrorEnum.None,
  };
  for (let index = 0; index < conditionsObject.condition.length; index++) {
    const condition = conditionsObject.condition[index];

    if (blankIsOk && koddel.kontodel === '') {
      result.isValid = true;
      continue;
    }

    if (condition.op === 'isnull' || isConditionDefined(condition.values)) {
      result.values = condition.values;
      switch (condition.op) {
        //copInterval
        case 'interval':
          result.isValid = condition.values.some(conditionValue => {
            return koddelInInterval(koddel, conditionValue);
          });
          break;

        // copNotInterval
        case 'notinterval':
          result.isValid = condition.values.every(conditionValue => {
            return !koddelInInterval(koddel, conditionValue);
          });

          break;

        //copEqual
        case 'equals':
          result.isValid = condition.values.some(conditionValue => {
            if (conditionValue.value) {
              const value = conditionValue.value.toString();
              return koddel.kontodel === value;
            } else {
              return false;
            }
          });

          break;

        //copNotEqual
        case 'notequals':
          result.isValid = condition.values.every(conditionValue => {
            if (conditionValue.value) {
              const value = conditionValue.value.toString();
              return koddel.kontodel !== value;
            } else {
              return false;
            }
          });

          break;

        // copGreater
        case 'greater':
          result.isValid = condition.values.some(conditionValue => {
            if (conditionValue.value) {
              const value = conditionValue.value.toString();
              return koddel.kontodel > value;
            } else {
              return false;
            }
          });

          break;

        // copGreaterEqual
        case 'notless':
          result.isValid = condition.values.some(conditionValue => {
            if (conditionValue.value) {
              const value = conditionValue.value.toString();
              return koddel.kontodel >= value;
            } else {
              return false;
            }
          });

          break;

        // copLessEqual
        case 'notgreater':
          result.isValid = condition.values.some(conditionValue => {
            if (conditionValue.value) {
              const value = conditionValue.value.toString();
              return koddel.kontodel <= value;
            } else {
              return false;
            }
          });

          break;

        // copLess
        case 'less':
          result.isValid = condition.values.some(conditionValue => {
            if (conditionValue.value) {
              const value = conditionValue.value.toString();
              return koddel.kontodel < value;
            } else {
              return false;
            }
          });

          break;

        // copIsNull
        case 'isnull':
          result.isValid = koddel.kontodel === '';

          break;

        // copHasValue
        case 'hasvalue':
          result.isValid = koddel.kontodel !== '';

          break;

        //copStartingWith
        case 'starts':
          result.isValid = condition.values.some(conditionValue => {
            if (conditionValue.value) {
              return koddelStartsWith(koddel, conditionValue.value);
            } else {
              return false;
            }
          });
          break;

        //copNotStartingWith
        case 'notstarts':
          result.isValid = condition.values.every(conditionValue => {
            if (conditionValue.value) {
              return !koddelStartsWith(koddel, conditionValue.value);
            } else {
              return false;
            }
          });
          break;
        //copLike
        case 'contains':
          result.isValid = condition.values.some(conditionValue => {
            if (conditionValue.value) {
              const value = conditionValue.value.toString();
              return koddel.kontodel.includes(value);
            } else {
              return false;
            }
          });

          break;
        //copNotLike
        case 'notcontains':
          result.isValid = condition.values.every(conditionValue => {
            if (conditionValue.value) {
              const value = conditionValue.value.toString();
              return !koddel.kontodel.includes(value);
            } else {
              return false;
            }
          });
          break;

        default:
          break;
      }

      result.errorType = result.isValid
        ? UrvalErrorEnum.None
        : koddel.kontodel === ''
        ? UrvalErrorEnum.SaknarVarde
        : UrvalErrorEnum.SaknarKodbehorighet;
    } else {
      result.isValid = true;
    }
  }

  return result;
};

const kontostrangInPage = (
  kontoplan: IKontoplan,
  koddelar: IKodstrangDel[],
  page: IUIPage,
  blankIsOk?: boolean
): IConditionResult[] => {
  const result: IConditionResult[] = [];

  const sortedKoddelar = fillRemainingKoddelar(kontoplan, koddelar);

  for (let index = 0; index < sortedKoddelar.length; index++) {
    const koddel = sortedKoddelar[index];
    for (
      let conditionIndex = 0;
      conditionIndex < page.conditionObjects.length;
      conditionIndex++
    ) {
      const condition = page.conditionObjects[conditionIndex];
      if (
        determineIfIsValueConditionObjectType(condition) &&
        condition.attribute.AttributeName ===
          'KONTODEL_' + (index + 1).toString()
      ) {
        const kontodelInConditionResult = kontodelInCondition(
          koddel,
          condition,
          blankIsOk
        );
        result.push(kontodelInConditionResult);
      }
    }
  }

  return result;
};

const getAttributeErrors = (
  kontoplan: IKontoplan,
  koddelar: IKodstrangDel[] | IKontodel[],
  page: IUIPage,
  blankIsOk?: boolean
): IAttributeErrors[] => {
  const result: IAttributeErrors[] = [];

  const pageResults = kontostrangInPage(kontoplan, koddelar, page, blankIsOk);

  for (let index = 0; index < pageResults.length; index++) {
    const pageResult = pageResults[index];
    if (!pageResult.isValid) {
      const errorTexts = pageResult.values.map(x => {
        const toValue = x.toValue ? `-${x.toValue}` : '';
        return `${x.value}${toValue}`;
      });

      const error: IAttributeErrors = {
        name: pageResult.conditionAttributeName,
        values: errorTexts,
        errorType: pageResult.errorType,
      };

      result.push(error);
    }
  }

  return result;
};

export const kontostrangInSelection = (
  kontoplan: IKontoplan,
  koddelar: IKodstrangDel[] | IKontodel[],
  attestKodbehUrval: IUIUrval,
  blankIsOk?: boolean
): IAttributeErrors[] => {
  let result: IAttributeErrors[] = [];

  for (
    let pageIndex = 0;
    pageIndex < attestKodbehUrval.pages.length;
    pageIndex++
  ) {
    const page = attestKodbehUrval.pages[pageIndex];
    const errorResult = getAttributeErrors(
      kontoplan,
      koddelar,
      page,
      blankIsOk
    );

    if (errorResult && errorResult.length === 0) {
      return [];
    } else {
      result = result.concat(errorResult);
    }
  }
  return result;
};

export const filterKodBehOnKoddel = (
  koddel: IKodstrangDel,
  attestKodbehUrval: IUIUrval
): boolean => {
  let isValid: boolean = true;

  for (
    let pageIndex = 0;
    pageIndex < attestKodbehUrval.pages.length;
    pageIndex++
  ) {
    const page = attestKodbehUrval.pages[pageIndex];
    isValid = true;

    for (
      let conditionIndex = 0;
      conditionIndex < page.conditionObjects.length;
      conditionIndex++
    ) {
      const condition = page.conditionObjects[conditionIndex];
      if (
        determineIfIsValueConditionObjectType(condition) &&
        condition.attribute.AttributeName.includes(koddel.ordningsNr.toString())
      ) {
        if (!kontodelInCondition(koddel, condition).isValid) {
          isValid = false;
        }
      }
    }

    if (isValid) {
      return isValid;
    }
  }

  return isValid;
};

export interface IFilterKodbehOnKodstrangResult {
  attestKodbehorighetUrval: IAttestKodbehorighetUrval[];
  correctnessKodbehorighetUrval: IAttestKodbehorighetUrval[];
}

export const filterKodbehOnKodstrang = (
  ordinarieAttestantKodbehorigheter: IAttestKodbehorighetUrval[] | undefined,
  kodstrang: IKodstrang
): IFilterKodbehOnKodstrangResult => {
  let result: IFilterKodbehOnKodstrangResult = {
    attestKodbehorighetUrval: [],
    correctnessKodbehorighetUrval: [],
  };

  let highestCorrectnessObjectArray: ICorrectnessKodbeh[] | null = null;

  if (ordinarieAttestantKodbehorigheter) {
    const correctnessObjectArray: ICorrectnessKodbeh[] = [];
    for (
      let attestKodbehUrvalIndex = 0;
      attestKodbehUrvalIndex < ordinarieAttestantKodbehorigheter.length;
      attestKodbehUrvalIndex++
    ) {
      let isKodbehValid: boolean = true;
      const attestKodBeh =
        ordinarieAttestantKodbehorigheter[attestKodbehUrvalIndex];
      const attestKodbehUrval = attestKodBeh.urval;

      const correctnessObject: ICorrectnessKodbeh = {
        kodbehoriget: attestKodBeh,
        correctnessCount: 0,
      };
      for (
        let koddelIndex = 0;
        koddelIndex < kodstrang?.kontodelar.length;
        koddelIndex++
      ) {
        const koddel = kodstrang.kontodelar[koddelIndex];

        const isKoddelOk = filterKodBehOnKoddel(koddel, attestKodbehUrval);

        if (isKoddelOk) {
          correctnessObject.correctnessCount += 1;
        }

        if (!isKodbehValid) {
          continue;
        }
        isKodbehValid = isKoddelOk;
      }
      if (isKodbehValid) {
        result.attestKodbehorighetUrval.push(
          ordinarieAttestantKodbehorigheter[attestKodbehUrvalIndex]
        );
      }

      correctnessObjectArray.push(correctnessObject);
    }

    // Fyll på en array med kodbehörigheterna som har högst correctnessCount
    if (correctnessObjectArray.length > 0) {
      highestCorrectnessObjectArray = correctnessObjectArray.reduce(
        (
          higestCorrectnessObjectArray: ICorrectnessKodbeh[],
          currentObj: ICorrectnessKodbeh,
          currentIndex: number
        ) => {
          if (currentIndex === 0) {
            return [currentObj];
          }

          if (
            higestCorrectnessObjectArray[0]?.correctnessCount ===
            currentObj.correctnessCount
          ) {
            return [...higestCorrectnessObjectArray, currentObj];
          }

          if (
            currentObj.correctnessCount >
            higestCorrectnessObjectArray[0]?.correctnessCount
          ) {
            return [currentObj];
          }

          return higestCorrectnessObjectArray;
        },
        []
      );
    }
    result.correctnessKodbehorighetUrval =
      highestCorrectnessObjectArray?.map(
        correctnessObject => correctnessObject.kodbehoriget
      ) ?? [];
  }

  return result;
};
